// Client-side constraints for the Custom Form file-upload field (D-005).
//
// Deliberately identical to the card owner's own resume upload in MyCard (same
// accept list, same 5MB ceiling, same FileReader.readAsDataURL capture), so a
// visitor meets the constraints owners already know from the owner-side
// feature. This is UX only: server/attachments.ts is the enforcement, since the
// submit endpoint is public and anonymous.
//
// Lives outside utils/api.ts on purpose — api.ts is mocked wholesale in several
// component tests, and constants imported from a mocked module come back
// undefined.

export const ATTACHMENT_PDF_TYPE = 'application/pdf';
export const ATTACHMENT_DOCX_TYPE =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

/** The exact accept list MyCard uses for the owner's own resume upload. */
export const ATTACHMENT_ACCEPT =
  '.pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

export const ATTACHMENT_MAX_BYTES = 5 * 1024 * 1024;

const ALLOWED_TYPES = [ATTACHMENT_PDF_TYPE, ATTACHMENT_DOCX_TYPE];
const ALLOWED_EXTENSIONS = ['.pdf', '.docx'];

/** What gets sent as the submission's `attachment`. */
export interface CapturedAttachment {
  /** data: URL from FileReader — the server strips the prefix before storing. */
  data: string;
  filename: string;
  type: string;
}

/**
 * Why this file can't be attached, or null if it can.
 *
 * Some browsers report an empty `type` for .docx, so the extension is used as a
 * fallback signal — never as an override for a type that is present and wrong.
 */
export function validateAttachmentFile(file: File | null | undefined): string | null {
  if (!file) return 'Choose a file to attach.';

  const name = (file.name || '').toLowerCase();
  const typeOk = file.type
    ? ALLOWED_TYPES.includes(file.type)
    : ALLOWED_EXTENSIONS.some((ext) => name.endsWith(ext));
  if (!typeOk) return 'Attachment must be a PDF or DOCX file.';

  if (file.size > ATTACHMENT_MAX_BYTES) return 'Attachment must be under 5MB.';

  return null;
}

/**
 * The MIME type to actually send for this file.
 *
 * validateAttachmentFile accepts a file whose `type` the browser left empty
 * (common for .docx) on the strength of its extension, so the submitted type
 * has to be resolved the same way — the server checks its allowlist before
 * looking at content, and an empty string would be rejected there with nothing
 * to tell the visitor which field was at fault.
 *
 * Only fills a MISSING type; a type that is present and wrong is left alone for
 * validateAttachmentFile to reject, so an extension can never launder a
 * disallowed file past the check.
 */
export function resolveAttachmentType(file: File): string {
  if (file.type) return file.type;
  return (file.name || '').toLowerCase().endsWith('.docx')
    ? ATTACHMENT_DOCX_TYPE
    : ATTACHMENT_PDF_TYPE;
}

/**
 * Restamp a FileReader data URL with the resolved type.
 *
 * When a file's own `type` is empty, readAsDataURL labels the payload
 * `application/octet-stream`. The server requires a data URL's declared mime to
 * agree with the `type` field — a disagreement is treated as spoofing, which is
 * the right boundary — so the prefix has to be brought in line here rather than
 * relaxing that check.
 */
export function normalizeAttachmentDataUrl(dataUrl: string, type: string): string {
  const comma = dataUrl.indexOf(',');
  if (comma === -1) return dataUrl;
  return `data:${type};base64,${dataUrl.slice(comma + 1)}`;
}

/** FileReader.readAsDataURL as a promise, matching the owner-resume capture. */
export function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (ev) => resolve(ev.target?.result as string);
    reader.onerror = () => reject(new Error('Could not read that file.'));
    reader.readAsDataURL(file);
  });
}
